import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { ArrowRightLeft, CheckCircle, AlertCircle, X, Loader, AlertTriangle, SlidersHorizontal, Copy, Check, Play, Pause, Database, Shuffle } from "lucide-react";
import api from "~/services/api";

const STATUSES = ["Inactive", "In Class", "Waiting"];

const STATUS_BADGE = {
  Inactive: "bg-red-100 text-red-700",
  "In Class": "bg-blue-100 text-blue-700",
  Waiting: "bg-yellow-100 text-yellow-700",
};

const STEP_BADGE = {
  created: "bg-green-100 text-green-700",
  updated: "bg-amber-100 text-amber-700",
  skipped: "bg-gray-100 text-gray-500",
  error: "bg-red-100 text-red-700",
};

const STEP_FIELDS = {
  enrollment: [
    { label: "Step", key: "form_step" },
    { label: "Application", key: "application" },
    { label: "Plan Date", key: "plan_date" },
  ],
  contract: [
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
  ],
  activation: [
    { label: "Date", key: "activation_date" },
  ],
  inactivation: [
    { label: "Date", key: "date" },
    { label: "Reason", key: "reason" },
  ],
};

const defaultGridHeader = [
  { title: "Name", name: "name", filter: false },
  { title: "Registration", name: "registration_number", filter: false },
  { title: "Status", name: "status", filter: true },
  { title: "Previously Migrated", name: "enrollment_id", filter: false },
];

// ─── StatusCard ──────────────────────────────────────────────────────────────

function StatusCard({ label, data, fields }) {
  const ok = !!data;
  return (
    <div className={`rounded-lg border p-3 flex flex-col gap-1 ${ok ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`}>
      <div className="flex flex-row items-center gap-1 font-bold text-xs mb-1">
        {ok ? <CheckCircle size={12} className="text-green-600" /> : <AlertCircle size={12} className="text-gray-400" />}
        {label}
      </div>
      {ok ? (
        fields.map(({ label: fl, key }) => (
          <div key={key} className="text-xs text-gray-600">
            <span className="font-medium">{fl}:</span> {data[key] ?? "—"}
          </div>
        ))
      ) : (
        <div className="text-xs text-gray-400 italic">Not created</div>
      )}
    </div>
  );
}

// ─── Error Logs Modal ─────────────────────────────────────────────────────────

function ErrorCell({ message, mono = false }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(message || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex flex-row items-center gap-2 max-w-sm">
      <span className={`truncate flex-1 ${mono ? "font-mono text-gray-600" : "text-red-600"}`} title={message}>
        {message || "—"}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        title="Copy"
        className={`shrink-0 flex items-center gap-1 rounded px-1.5 h-5 text-xs transition-all ${
          copied ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
        }`}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
  );
}

function ErrorLogsModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regFilter, setRegFilter] = useState("");
  const [applied, setApplied] = useState("");

  async function fetchLogs(registration_number) {
    setLoading(true);
    try {
      const params = registration_number ? { registration_number } : {};
      const { data } = await api.get("/students/migration-error-logs", { params });
      setLogs(data.logs || []);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to load error logs", { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchLogs(""); }, []);

  function handleSearch() {
    setApplied(regFilter.trim());
    fetchLogs(regFilter.trim());
  }

  function handleClear() {
    setRegFilter("");
    setApplied("");
    fetchLogs("");
  }

  function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
      + " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }

  return (
    <div
      className="fixed z-50 bg-white shadow-lg border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "80%", maxWidth: "92%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-500" /> Migration Error Logs
          {!loading && (
            <span className="ml-1 text-xs font-normal text-gray-500">— {logs.length} record(s)</span>
          )}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex flex-row items-center gap-2 px-4 py-2 border-b shrink-0">
        <input
          type="text"
          value={regFilter}
          onChange={(e) => setRegFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Filter by Registration..."
          className="bg-secondary rounded-sm h-8 px-3 text-xs text-gray-600 focus:outline-none w-72"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="text-xs border border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white rounded-sm px-3 h-8 font-bold"
        >
          Filter
        </button>
        {applied && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs border rounded-sm px-3 h-8 text-gray-500 hover:bg-gray-100"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={20} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No error logs found.</p>
        ) : (
          <table className="w-full table-auto text-xs text-left whitespace-nowrap">
            <thead className="sticky top-0 bg-secondary">
              <tr className="h-8">
                <th className="px-4">Migration Date</th>
                <th className="px-4">Created At</th>
                <th className="px-4">Registration</th>
                <th className="px-4">Student ID</th>
                <th className="px-4">Student Status</th>
                <th className="px-4">Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t odd:bg-white h-10">
                  <td className="px-4 text-gray-500">{formatDate(log.migration_date)}</td>
                  <td className="px-4 text-gray-500">{formatDate(log.created_at)}</td>
                  <td className="px-4 font-medium text-gray-700">{log.registration_number || "—"}</td>
                  <td className="px-4">
                    <ErrorCell message={log.student_id} mono />
                  </td>
                  <td className="px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[log.student_status] || "bg-gray-100 text-gray-500"}`}>
                      {log.student_status}
                    </span>
                  </td>
                  <td className="px-4">
                    <ErrorCell message={log.error_message} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Migration Result Preview ─────────────────────────────────────────────────

function MigrationResultPreview({ migratedIds, migrateResults, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDetails() {
      try {
        const { data } = await api.post("/students/migration-result", { student_ids: migratedIds });
        setDetails(data);
      } catch (err) {
        toast(err?.response?.data?.error || "Failed to load results", { type: "error", autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    }
    fetchDetails();
  }, []);

  const migrateByStudent = useMemo(() => {
    if (!migrateResults?.results) return {};
    return Object.fromEntries(migrateResults.results.map((r) => [r.student_id, r]));
  }, [migrateResults]);

  return (
    <div
      className="fixed z-40 right-0 bg-white shadow-lg border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "62%", maxWidth: "84%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <ArrowRightLeft size={16} /> Migration Results
          {details && (
            <span className="ml-1 text-xs font-normal text-gray-500">— {details.total} student(s)</span>
          )}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={22} className="animate-spin" />
            <span className="text-sm">Loading results...</span>
          </div>
        ) : !details?.results?.length ? (
          <p className="text-sm text-gray-400 text-center py-12">No results found.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {details.results.map((r) => {
              const migrateRow = migrateByStudent[r.student_id];
              const steps = migrateRow?.steps || {};
              const stepKeys = Object.keys(steps);
              return (
                <div key={r.student_id} className="border rounded-xl p-4 flex flex-col gap-3">
                  <div className="flex flex-row items-center flex-wrap gap-2 border-b pb-2">
                    <span className="font-bold text-sm">{r.name}</span>
                    <span className="text-xs text-gray-500">{r.registration_number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status] || "bg-gray-100 text-gray-500"}`}>
                      {r.status}
                    </span>
                    {migrateRow && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        migrateRow.status === "success" ? "bg-green-100 text-green-700" :
                        migrateRow.status === "skipped" ? "bg-gray-100 text-gray-500" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {migrateRow.status}
                        {migrateRow.reason ? ` — ${migrateRow.reason}` : ""}
                      </span>
                    )}
                    {stepKeys.length > 0 && (
                      <div className="ml-auto flex flex-row flex-wrap gap-1">
                        {stepKeys.map((step) => (
                          <span key={step} className={`px-2 py-0.5 rounded-full text-xs font-medium ${STEP_BADGE[steps[step]] || "bg-gray-100 text-gray-400"}`}>
                            {step}: {steps[step]}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={`grid gap-3 ${stepKeys.length <= 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"}`}>
                    {["enrollment", "contract", "activation", "inactivation"]
                      .filter((step) => stepKeys.length === 0 || stepKeys.includes(step) || r[step])
                      .map((step) => (
                        <StatusCard
                          key={step}
                          label={step.charAt(0).toUpperCase() + step.slice(1)}
                          data={r[step]}
                          fields={STEP_FIELDS[step]}
                        />
                      ))
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Migrate All Modal ────────────────────────────────────────────────────────

function MigrateAllModal({ onClose }) {
  const { profile } = useSelector((state) => state.auth);
  const filials = (profile.filials || []).map(({ filial }) => filial);

  const [filialId, setFilialId] = useState(filials[0]?.id || "");
  const [status, setStatus] = useState("Inactive");
  const [limitInput, setLimitInput] = useState("");

  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);

  const [total, setTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errorRows, setErrorRows] = useState([]);

  const pausedRef = useRef(false);
  const nextOffsetRef = useRef(0);

  async function runLoop(offsetStart) {
    let offset = offsetStart;
    const limitVal = limitInput ? parseInt(limitInput, 10) : undefined;

    while (true) {
      if (pausedRef.current) break;

      const body = { filial_id: parseInt(filialId, 10), status, offset, batch_size: 100 };
      if (limitVal) body.limit = limitVal;

      let data;
      try {
        ({ data } = await api.post("/students/migrate-all", body));
      } catch (err) {
        toast(err?.response?.data?.error || "Migrate All failed", { type: "error", autoClose: 3000 });
        setRunning(false);
        return;
      }

      setTotal(data.total);
      setProcessed(data.offset + data.processed_in_batch);
      setSuccessCount((s) => s + data.success_in_batch);
      setErrorCount((e) => e + data.error_in_batch);
      nextOffsetRef.current = data.next_offset;

      const errors = (data.results || []).filter((r) => r.status === "error");
      if (errors.length > 0) setErrorRows((prev) => [...prev, ...errors]);

      if (data.done) {
        setDone(true);
        setRunning(false);
        return;
      }

      offset = data.next_offset;
    }

    setRunning(false);
  }

  function handleStart() {
    if (!filialId) {
      toast("Select a filial.", { type: "warning", autoClose: 2000 });
      return;
    }
    nextOffsetRef.current = 0;
    setRunning(true);
    setPaused(false);
    setDone(false);
    setProcessed(0);
    setSuccessCount(0);
    setErrorCount(0);
    setErrorRows([]);
    setTotal(0);
    pausedRef.current = false;
    runLoop(0);
  }

  function handlePause() {
    pausedRef.current = true;
    setPaused(true);
    setRunning(false);
  }

  function handleResume() {
    pausedRef.current = false;
    setPaused(false);
    setRunning(true);
    runLoop(nextOffsetRef.current);
  }

  const progress = total > 0 ? Math.min((processed / total) * 100, 100) : 0;
  const canStart = !running && !paused && !done;

  return (
    <div
      className="fixed z-50 bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "62%", maxWidth: "84%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <Database size={14} /> Migrate All
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={running}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200 disabled:opacity-40"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
        {/* Config */}
        <div className="flex flex-row items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Filial</label>
            <select
              value={filialId}
              onChange={(e) => setFilialId(e.target.value)}
              disabled={running || paused}
              className="bg-secondary rounded-sm h-8 px-2 text-xs text-gray-600 focus:outline-none min-w-48"
            >
              {filials.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={running || paused}
              className="bg-secondary rounded-sm h-8 px-2 text-xs text-gray-600 focus:outline-none"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Limit (optional)</label>
            <input
              type="number"
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
              disabled={running || paused}
              placeholder="All"
              className="bg-secondary rounded-sm h-8 px-2 text-xs text-gray-600 focus:outline-none w-24"
            />
          </div>

          <div className="flex flex-row gap-2 ml-auto">
            {canStart && (
              <button
                type="button"
                onClick={handleStart}
                className="flex items-center gap-2 border border-mila_orange text-mila_orange hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                <Play size={12} /> Start
              </button>
            )}
            {running && (
              <button
                type="button"
                onClick={handlePause}
                className="flex items-center gap-2 border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                <Pause size={12} /> Pause
              </button>
            )}
            {paused && (
              <button
                type="button"
                onClick={handleResume}
                className="flex items-center gap-2 border border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                <Play size={12} /> Resume
              </button>
            )}
            {(paused || done) && (
              <button
                type="button"
                onClick={handleStart}
                className="flex items-center gap-2 border border-gray-400 text-gray-500 hover:bg-gray-100 font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                Restart
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        {(running || paused || done || processed > 0) && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between text-xs text-gray-500">
              <span>
                {running && <Loader size={11} className="inline animate-spin mr-1" />}
                {done ? "Done — " : paused ? "Paused — " : ""}
                <span className="font-bold text-gray-700">{processed}</span> / {total || "?"} processed
              </span>
              <span className="flex gap-3">
                <span className="text-green-600 font-bold">{successCount} success</span>
                <span className="text-red-500 font-bold">{errorCount} errors</span>
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${done ? "bg-green-500" : paused ? "bg-amber-400" : "bg-mila_orange"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error table */}
        {errorRows.length > 0 && (
          <div className="flex flex-col gap-2">
            <h3 className="text-xs font-bold text-red-500 flex items-center gap-1">
              <AlertTriangle size={12} /> Errors ({errorRows.length})
            </h3>
            <div className="overflow-y-auto max-h-80 border rounded-lg">
              <table className="w-full table-auto text-xs text-left whitespace-nowrap">
                <thead className="sticky top-0 bg-secondary">
                  <tr className="h-8">
                    <th className="px-4">Registration</th>
                    <th className="px-4">Name</th>
                    <th className="px-4">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {errorRows.map((r, i) => (
                    <tr key={r.student_id || i} className="border-t odd:bg-white h-9">
                      <td className="px-4 font-medium text-gray-700">{r.registration_number || "—"}</td>
                      <td className="px-4 text-gray-600">{r.name || "—"}</td>
                      <td className="px-4 text-red-500">{r.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {done && errorRows.length === 0 && (
          <p className="text-sm text-green-600 font-medium text-center py-4">
            Migration completed with no errors.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Student Migration Detail Panel ──────────────────────────────────────────

const RESULT_STEP_FIELDS = {
  enrollment: [
    { label: "Application", key: "application" },
    { label: "Plan Date", key: "plan_date" },
    { label: "Created At", key: "created_at" },
  ],
  contract: [
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
    { label: "Updated At", key: "updated_at" },
  ],
  activation: [
    { label: "Activation Date", key: "activation_date" },
    { label: "Group ID", key: "student_group_id" },
  ],
  inactivation: [
    { label: "Date", key: "date" },
    { label: "Reason", key: "reason" },
  ],
};

function formatDetailDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

function normalizeDetailFields(step, data) {
  if (!data) return null;
  const normalized = { ...data };
  ["plan_date", "created_at", "updated_at", "activation_date", "date"].forEach((key) => {
    if (normalized[key]) normalized[key] = formatDetailDate(normalized[key]);
  });
  return normalized;
}

function StudentMigrationDetail({ studentId, onClose }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const { data } = await api.get(`/students/${studentId}/migration-result`);
        setResult(data);
      } catch (err) {
        toast(err?.response?.data?.error || "Failed to load student migration result", { type: "error", autoClose: 3000 });
        onClose();
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [studentId]);

  return (
    <div
      className="fixed z-50 bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "62%", maxWidth: "84%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <ArrowRightLeft size={14} /> Migration Detail
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={20} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : !result ? null : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1 border-b pb-3">
              <span className="font-bold text-sm text-gray-800">{result.name}</span>
              <span className="text-xs text-gray-500">{result.registration_number}</span>
              <span className={`mt-1 self-start px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[result.status] || "bg-gray-100 text-gray-500"}`}>
                {result.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {["enrollment", "contract", "activation", "inactivation"].map((step) => (
                <StatusCard
                  key={step}
                  label={step.charAt(0).toUpperCase() + step.slice(1)}
                  data={normalizeDetailFields(step, result[step])}
                  fields={RESULT_STEP_FIELDS[step]}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Migration Report ─────────────────────────────────────────────────────────

const REPORT_STEP_FIELDS = {
  enrollment: [
    { label: "Application", key: "application" },
    { label: "Plan Date", key: "plan_date" },
    { label: "Created At", key: "created_at" },
  ],
  contract: [
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
  ],
  activation: [
    { label: "Activation Date", key: "activation_date" },
  ],
  inactivation: [
    { label: "Date", key: "date" },
    { label: "Reason", key: "reason" },
  ],
};

function formatReportDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

function normalizeReportFields(step, data) {
  if (!data) return null;
  const n = { ...data };
  ["plan_date", "created_at", "date", "activation_date"].forEach((k) => {
    if (n[k]) n[k] = formatReportDate(n[k]);
  });
  return n;
}

function MigrationReport({ onMigrateAll, onErrorLogs }) {
  const [statusFilter, setStatusFilter] = useState("Inactive");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [resultPreview, setResultPreview] = useState(null);
  const [migrateResponse, setMigrateResponse] = useState(null);
  const debounceRef = useRef(null);
  const limit = 100;

  async function fetchReport(newOffset, newStatus, newSearch) {
    setLoading(true);
    try {
      const params = {
        status: newStatus ?? statusFilter,
        offset: newOffset ?? offset,
        limit,
      };
      const term = newSearch !== undefined ? newSearch : search;
      if (term.trim()) params.search = term.trim();
      const { data } = await api.get("/students/migration-report", { params });
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to load migration report", { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchReport(0, statusFilter, ""); }, []);

  function handleStatusChange(s) {
    setStatusFilter(s);
    setOffset(0);
    setSelectedIds(new Set());
    fetchReport(0, s, search);
  }

  function handleSearchChange(value) {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchReport(0, statusFilter, value);
    }, 400);
  }

  function handlePage(newOffset) {
    setOffset(newOffset);
    fetchReport(newOffset, statusFilter, search);
  }

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function selectRandom5() {
    const eligible = results.filter((r) => !r.migrated);
    const shuffled = [...eligible].sort(() => Math.random() - 0.5);
    setSelectedIds(new Set(shuffled.slice(0, 5).map((r) => r.student_id)));
  }

  async function handleMigrate() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast("Select at least one student to migrate.", { type: "warning", autoClose: 2000 });
      return;
    }
    setMigrating(true);
    try {
      const { data: migrateData } = await api.post("/students/migrate", { student_ids: ids });
      toast("Migration complete!", { type: "success", autoClose: 2000 });
      setMigrateResponse(migrateData);
      setResultPreview(ids);
      setSelectedIds(new Set());
      await fetchReport(offset, statusFilter);
    } catch (err) {
      toast(err?.response?.data?.error || "Migration failed", { type: "error", autoClose: 3000 });
    } finally {
      setMigrating(false);
    }
  }

  const filtered = results;

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const selectedCount = selectedIds.size;

  return (
    <div className="flex flex-1 flex-col w-full h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-row items-center justify-between py-2 px-0 gap-2 h-12 shrink-0">
        <div className="flex flex-row items-center gap-2">
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={handleMigrate}
              disabled={migrating}
              className="flex flex-row items-center gap-2 border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs px-3 h-8 disabled:opacity-50"
            >
              {migrating ? <Loader size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
              {migrating ? "Migrating..." : `Migrate Selected (${selectedCount})`}
            </button>
          )}
          <button
            type="button"
            onClick={selectRandom5}
            className="flex flex-row items-center gap-2 border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs px-3 h-8"
          >
            <Shuffle size={12} /> Select 5 Random
          </button>
          <button
            type="button"
            onClick={onMigrateAll}
            className="flex flex-row items-center gap-2 border border-sky-600 text-sky-600 transition-all hover:bg-sky-600 hover:text-white font-bold rounded-sm text-xs px-3 h-8"
          >
            <Database size={12} /> Migrate All
          </button>
          <button
            type="button"
            onClick={onErrorLogs}
            className="flex flex-row items-center gap-2 border border-red-400 text-red-500 transition-all hover:bg-red-500 hover:text-white font-bold rounded-sm text-xs px-3 h-8"
          >
            <AlertTriangle size={12} /> Error Logs
          </button>
          <span className="text-xs text-gray-500">
            <span className="font-bold text-sky-700">{total}</span> students
            {selectedCount > 0 && (
              <span className="ml-2 font-bold text-mila_orange">{selectedCount} selected</span>
            )}
          </span>
        </div>
        <div className="flex flex-row items-center gap-2">
          {totalPages > 1 && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              Page
              <select
                className="bg-secondary rounded-sm h-8 px-2 mx-1"
                value={currentPage}
                onChange={(e) => handlePage((Number(e.target.value) - 1) * limit)}
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              of <span className="font-bold text-sky-700 ml-1">{totalPages}</span>
            </p>
          )}
          <div className="flex items-center bg-secondary rounded-sm h-8 px-2 gap-2 min-w-36">
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search..."
              className="bg-transparent text-xs text-gray-500 focus:outline-none w-full"
            />
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setStatusPopoverOpen((o) => !o)}
              className={`flex flex-row items-center gap-2 rounded-sm h-8 px-2 transition ease-in-out whitespace-nowrap text-xs ${
                statusPopoverOpen
                  ? "-translate-y-1 scale-110 bg-sky-700 text-white"
                  : "bg-secondary text-gray-500 hover:bg-sky-700 hover:text-white"
              }`}
            >
              <SlidersHorizontal size={12} />
              {statusFilter}
            </button>
            {statusPopoverOpen && (
              <div className="absolute top-10 right-0 z-50 flex flex-col bg-secondary border border-gray-300 rounded-xl gap-1 shadow-xl p-2 text-xs">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { handleStatusChange(s); setStatusPopoverOpen(false); }}
                    className={`${statusFilter === s ? "bg-gray-300" : ""} hover:bg-gray-300 px-2 py-2 rounded text-left whitespace-nowrap`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => fetchReport(offset, statusFilter)}
            disabled={loading}
            className="text-xs border rounded-sm px-3 h-8 hover:bg-gray-100 text-gray-500 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={20} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No students found.</p>
        ) : (
          <table className="w-full table-auto text-xs bg-white rounded-xl overflow-hidden text-left whitespace-nowrap">
            <thead className="sticky top-0 bg-secondary">
              <tr className="h-8">
                <th className="px-4 w-10">Sel.</th>
                <th className="px-4">Registration</th>
                <th className="px-4">Name</th>
                <th className="px-4">Migrated</th>
                <th className="px-4">Enrollment</th>
                <th className="px-4">Contract</th>
                <th className="px-4">Activation</th>
                <th className="px-4">Inactivation</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isSelected = selectedIds.has(r.student_id);
                const isExpanded = expandedId === r.student_id;
                return (
                  <React.Fragment key={r.student_id}>
                    <tr
                      className={`border-t h-10 cursor-pointer hover:border hover:border-mila_orange ${isSelected ? "bg-mila_orange text-white" : "odd:bg-white"}`}
                      onClick={() => setExpandedId(isExpanded ? null : r.student_id)}
                    >
                      <td className="px-4 w-10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(r.student_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-4 font-medium">{r.registration_number}</td>
                      <td className="px-4">{r.name}</td>
                      <td className="px-4">
                        {r.migrated ? (
                          <span className={`flex items-center gap-1 ${isSelected ? "text-white" : "text-green-600"}`}>
                            <CheckCircle size={12} /> Yes
                          </span>
                        ) : (
                          <span className={isSelected ? "text-white/60" : "text-gray-400"}>—</span>
                        )}
                      </td>
                      {["enrollment", "contract", "activation", "inactivation"].map((step) => (
                        <td key={step} className="px-4">
                          {r[step] ? (
                            <CheckCircle size={12} className={isSelected ? "text-white" : "text-green-500"} />
                          ) : (
                            <AlertCircle size={12} className={isSelected ? "text-white/40" : "text-gray-300"} />
                          )}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && (
                      <tr className="border-t bg-gray-50">
                        <td colSpan={8} className="px-6 py-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {["enrollment", "contract", "activation", "inactivation"].map((step) => (
                              <StatusCard
                                key={step}
                                label={step.charAt(0).toUpperCase() + step.slice(1)}
                                data={normalizeReportFields(step, r[step])}
                                fields={REPORT_STEP_FIELDS[step]}
                              />
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Migration Result Overlay */}
      {resultPreview && (
        <div className="fixed left-0 top-0 z-40 w-full h-full" style={{ background: "rgba(0,0,0,.2)" }}>
          <MigrationResultPreview
            migratedIds={resultPreview}
            migrateResults={migrateResponse}
            onClose={() => { setResultPreview(null); setMigrateResponse(null); }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Migration Row ────────────────────────────────────────────────────────────

const MigrationRow = React.memo(function MigrationRow({ student, isSelected, onToggle, onOpenDetail }) {
  const alreadyMigrated = !!student.enrollment_id;
  return (
    <tr
      className={`border-t h-10 hover:border hover:border-mila_orange cursor-pointer ${isSelected ? "bg-mila_orange text-white" : "odd:bg-white"}`}
      onClick={() => onOpenDetail(student.student_id)}
    >
      <td className="px-4 w-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(student.student_id)}
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer"
        />
      </td>
      <td className="px-4 text-xs">{student.name}</td>
      <td className="px-4 text-xs">{student.registration_number}</td>
      <td className="px-4 text-xs">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isSelected ? "bg-white/20 text-white" : STATUS_BADGE[student.status] || "bg-gray-100 text-gray-500"}`}>
          {student.status}
        </span>
      </td>
      <td className="px-4 text-xs">
        {alreadyMigrated ? (
          <span className={`flex items-center gap-1 ${isSelected ? "text-white" : "text-amber-600"}`}>
            <CheckCircle size={12} /> Yes
          </span>
        ) : (
          <span className={isSelected ? "text-white" : "text-gray-400"}>—</span>
        )}
      </td>
    </tr>
  );
});

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Migration() {
  const [errorLogsOpen, setErrorLogsOpen] = useState(false);
  const [migrateAllOpen, setMigrateAllOpen] = useState(false);

  return (
    <div className="flex flex-1 flex-col w-full h-full overflow-hidden">
      <MigrationReport
        onMigrateAll={() => setMigrateAllOpen(true)}
        onErrorLogs={() => setErrorLogsOpen(true)}
      />

      {/* Migrate All Overlay */}
      {migrateAllOpen && (
        <div className="fixed left-0 top-0 z-50 w-full h-full" style={{ background: "rgba(0,0,0,.2)" }}>
          <MigrateAllModal onClose={() => setMigrateAllOpen(false)} />
        </div>
      )}

      {/* Error Logs Overlay */}
      {errorLogsOpen && (
        <div className="fixed left-0 top-0 z-40 w-full h-full" style={{ background: "rgba(0,0,0,.2)" }}>
          <ErrorLogsModal onClose={() => setErrorLogsOpen(false)} />
        </div>
      )}
    </div>
  );
}
